import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  Typography,
  Paper,
  Stack,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { PrivateMainLayout } from "@/components/layout";
import { isMobile } from "react-device-detect";
import { LargeContentBox } from "@/components/common";
import { HealthAndSafety } from "@mui/icons-material";
import {
  AffiliationModal,
  AffiliationFormData,
} from "./components/AffiliationModal";
import { OrganizationAffiliation } from "fhir/r5";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useOrganizationAffiliation } from "@/services/fhir/organizationaffiliation/useOrganizationAffiliation";
import { PatchOrganizationAffiliation } from "@/services/fhir/organizationaffiliation/PatchOrganizationAffiliation";
import { AffiliationDetailsModal } from "./components/AffiliationDetailsModal";
import { AcceptedPlansTab } from "./components/AcceptedPlansTab";

import { useLocationContext } from "@/contexts/LocationContext";
import { useAppointmentStats } from "@/services/fhir/statistics/hooks/useAppointmentStats";
import { PageHeader } from "@/components/common/PageHeader";
import { ConfirmDeleteModal } from "./components/ConfirmDeleteModal";

/**
 * Transforma dados FHIR OrganizationAffiliation para o formato esperado pelo AffiliationModal
 * @param fhirData - Dados no formato FHIR OrganizationAffiliation
 * @returns Dados no formato AffiliationFormData com id
 */
const transformFHIROrganizationAffiliationToFormData = (
  fhirData: OrganizationAffiliation
): Partial<AffiliationFormData> & { id: string } => {
  // Extrai o ID do plano de saúde da extensão
  const insurancePlanExtension = fhirData.extension?.find(
    (ext) =>
      ext.url ===
      "https://fluxmed.com.br/fhir/StructureDefinition/insurance-plan-affiliation"
  );

  const insurancePlanReference =
    insurancePlanExtension?.valueReference?.reference || "";
  const insurancePlanId = insurancePlanReference.replace("InsurancePlan/", "");
  const insurancePlanDisplay =
    insurancePlanExtension?.valueReference?.display || "";

  // Extrai o ID da organização participante (prestador)
  const participatingOrgReference =
    fhirData.participatingOrganization?.reference || "";
  const participatingOrgId = participatingOrgReference.replace(
    "Organization/",
    ""
  );
  const participatingOrgDisplay =
    fhirData.participatingOrganization?.display || "";

  // Transforma os procedimentos (specialty) para o formato esperado
  const procedures =
    fhirData.specialty?.map((specialty) => {
      const coding = specialty.coding?.[0];
      const serviceValue = specialty.extension?.find(
        (ext) =>
          ext.url ===
          "https://fluxmed.com.br/fhir/StructureDefinition/service-value"
      )?.valueMoney?.value;

      return {
        code: coding?.code || "",
        display: coding?.display || "",
        label: coding?.display || "",
        value: coding?.code || "",
        serviceCost: serviceValue || 0,
      };
    }) || [];

  return {
    id: fhirData.id || "",
    institutionRegistration: fhirData.identifier?.[0]?.value || "",
    organization: {
      value: participatingOrgId,
      label: participatingOrgDisplay,
    },
    insurancePlans: {
      value: insurancePlanId,
      label: insurancePlanDisplay,
      display: insurancePlanDisplay,
    },
    procedures: procedures.length > 0 ? procedures : undefined,
  };
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`insurance-tabpanel-${index}`}
      aria-labelledby={`insurance-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

interface Filters {
  organization: string;
  plan: string;
  procedure: string;
}

// Função auxiliar para agrupar afiliações por organização
const groupAffiliationsByOrganization = (
  affiliations: OrganizationAffiliation[],
  filters: Filters
) => {
  const normalizedFilters = {
    organization: filters.organization.toLowerCase().trim(),
    plan: filters.plan.toLowerCase().trim(),
    procedure: filters.procedure.toLowerCase().trim(),
  };

  const filteredAffiliations = affiliations.filter((aff) => {
    const organizationName =
      aff.participatingOrganization?.display?.toLowerCase() || "";
    const planName =
      aff.extension
        ?.find(
          (ext) =>
            ext.url ===
            "https://fluxmed.com.br/fhir/StructureDefinition/insurance-plan-affiliation"
        )
        ?.valueReference?.display?.toLowerCase() || "";
    const procedures = (aff.specialty || [])
      .map((specialty) => specialty.coding?.[0]?.display?.toLowerCase() || "")
      .join(" ");

    return (
      (normalizedFilters.organization === "" ||
        organizationName.includes(normalizedFilters.organization)) &&
      (normalizedFilters.plan === "" ||
        planName.includes(normalizedFilters.plan)) &&
      (normalizedFilters.procedure === "" ||
        procedures.includes(normalizedFilters.procedure))
    );
  });

  return filteredAffiliations.reduce((groups, aff) => {
    const orgId = aff.participatingOrganization?.reference;
    if (!groups[orgId || ""]) {
      groups[orgId || ""] = {
        organizationName: aff.participatingOrganization?.display || "Sem nome",
        affiliations: [],
      };
    }
    groups[orgId || ""].affiliations.push(aff);
    return groups;
  }, {} as Record<string, { organizationName: string; affiliations: OrganizationAffiliation[] }>);
};

// Componente principal da tela de planos de saúde
export function ManagePlans() {
  const { organization } = useOrganizationContext();
  const { location } = useLocationContext();
  const [currentTab, setCurrentTab] = useState(0);
  const [filters, setFilters] = useState<Filters>({
    organization: "",
    plan: "",
    procedure: "",
  });

  // Estado para modal de cadastro/edição
  const [openModal, setOpenModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [affiliationToEdit, setAffiliationToEdit] =
    useState<OrganizationAffiliation | null>(null);

  // Estado para modal de detalhes
  const [openDetails, setOpenDetails] = useState(false);
  const [selectedAffiliation, setSelectedAffiliation] =
    useState<OrganizationAffiliation | null>(null);

  const [affiliationToPatch, setAffiliationToPatch] =
    useState<OrganizationAffiliation | null>(null);

  const {
    organizationAffiliation,

    loading,
    fetchOrganizationAffiliation,
  } = useOrganizationAffiliation();

  const handleOpenModal = () => {
    setIsEditing(false);
    setAffiliationToEdit(null);
    setOpenModal(true);
  };

  const handleEdit = (aff: OrganizationAffiliation) => {
    setIsEditing(true);
    setAffiliationToEdit(aff);
    setOpenModal(true);
  };

  const toggleActive = (aff: OrganizationAffiliation) => {
    setAffiliationToPatch(aff || null);
  };

  const handleOpenDetails = (aff: OrganizationAffiliation) => {
    setSelectedAffiliation(aff);
    setOpenDetails(true);
  };

  const handleCloseDetails = () => {
    setOpenDetails(false);
    setSelectedAffiliation(null);
  };

  const handleRefetch = () => {
    fetchOrganizationAffiliation({
      primaryOrganization: organization?.id,
    });
  };

  const handleFilterChange = (field: keyof Filters, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Effects
  useEffect(() => {
    if (organization?.id) {
      fetchOrganizationAffiliation({
        primaryOrganization: organization.id,
        count: 50,
        includePrimaryOrg: true,
        includeParticipatingOrg: true,
      });
    }
  }, [organization?.id]);

  return (
    <LargeContentBox>
      <Stack sx={{ width: "100%" }} spacing={3}>
        <PageHeader
          title="Planos de Saúde"
          icon={HealthAndSafety}
          action={
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenModal}
            >
              Novo Plano de Saúde
            </Button>
          }
        />

        {/* Filtros */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Filtros
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Filtrar por operadora..."
              value={filters.organization}
              onChange={(e) =>
                handleFilterChange("organization", e.target.value)
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Filtrar por plano..."
              value={filters.plan}
              onChange={(e) => handleFilterChange("plan", e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Filtrar por procedimento..."
              value={filters.procedure}
              onChange={(e) => handleFilterChange("procedure", e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </Paper>

        <AcceptedPlansTab
          loading={loading}
          organizationAffiliation={organizationAffiliation}
          filters={filters}
          onEdit={handleEdit}
          onToggleActive={toggleActive}
          onViewDetails={handleOpenDetails}
        />

        {/* Modals */}
        {openModal && (
          <AffiliationModal
            open={openModal}
            isEditing={isEditing}
            handleRefetch={handleRefetch}
            initialData={
              isEditing && affiliationToEdit
                ? transformFHIROrganizationAffiliationToFormData(
                    affiliationToEdit
                  )
                : undefined
            }
            onClose={() => {
              setOpenModal(false);
            }}
          />
        )}

        {openDetails && selectedAffiliation && (
          <AffiliationDetailsModal
            open={openDetails}
            onClose={handleCloseDetails}
            affiliation={selectedAffiliation}
          />
        )}
        {affiliationToPatch && (
          <ConfirmDeleteModal
            open={!!affiliationToPatch}
            onClose={() => setAffiliationToPatch(null)}
            handleRefetch={handleRefetch}
            affiliation={affiliationToPatch}
          />
        )}
      </Stack>
    </LargeContentBox>
  );
}
